<template>
    <div class="bg-color rounded-5 p-5">
        <div class="w-100 rounded-4">
            <div
                class="d-flex justify-content-between align-items-center"
                style="background: rgb(241, 247, 255); border-radius: 27px 27px 0 0"
            >
                <h1 class="px-5 py-3 page-heading">{{ isNewMode ? 'Create New Event' : 'Edit Event' }}</h1>
            </div>

            <!-- Full-form loader while dashboards / event data load -->
            <div v-if="isLoading" class="bg-white d-flex justify-content-center align-items-center" style="min-height: 340px">
                <div class="spinner-border text-primary" role="status"></div>
            </div>

            <div v-else class="bg-white p-5 rounded-bottom-4">
                <!-- Row 1: Event Name | Location -->
                <div class="form-row mb-4">
                    <div class="form-col">
                        <label class="field-label">Event Name <span class="text-danger">*</span></label>
                        <input
                            type="text"
                            v-model="form.name"
                            class="form-control"
                            placeholder="Enter Event Name"
                        />
                        <span class="text-danger field-error" v-if="errors.name">{{ errors.name }}</span>
                    </div>
                    <div class="form-col">
                        <label class="field-label">Location</label>
                        <input
                            type="text"
                            v-model="form.location"
                            class="form-control"
                            placeholder="Enter Location"
                        />
                    </div>
                </div>

                <!-- Row 2: Event Date -->
                <div class="form-row mb-4">
                    <div class="form-col">
                        <label class="field-label">Event Date <span class="text-danger">*</span></label>
                        <div class="date-input-wrapper">
                            <input
                                type="text"
                                class="form-control date-display"
                                :value="form.event_date ? formatDateDisplay(form.event_date) : ''"
                                placeholder="MM/DD/YYYY"
                                readonly
                            />
                            <span class="material-icons-outlined date-icon">calendar_today</span>
                            <input
                                ref="eventDatePicker"
                                type="date"
                                v-model="form.event_date"
                                class="date-hidden-picker"
                            />
                        </div>
                        <span class="text-danger field-error" v-if="errors.event_date">{{ errors.event_date }}</span>
                    </div>
                    <div class="form-col">
                        <label class="field-label">Street Team Dashboard <span class="text-danger">*</span></label>
                        <Multiselect
                            v-model="form.dashboardObj"
                            :options="dashboardOptions"
                            track-by="id"
                            label="dashboardName"
                            placeholder="Select Street Team Dashboard"
                            :searchable="true"
                            :allow-empty="true"
                            :show-labels="false"
                            style="width: 105%"
                        />
                        <span class="text-danger field-error" v-if="errors.dashboardObj">{{ errors.dashboardObj }}</span>
                    </div>
                </div>

                <!-- Row 3: Member Dashboard -->
                <div class="form-row mb-4">
                    <div class="form-col">
                        <label class="field-label">Member Dashboard <span class="text-danger">*</span></label>
                        <Multiselect
                            v-model="form.memberDashboardObj"
                            :options="dashboardOptions"
                            track-by="id"
                            label="dashboardName"
                            placeholder="Select Member Dashboard"
                            :searchable="true"
                            :allow-empty="true"
                            :show-labels="false"
                            style="width: 105%"
                        />
                        <span class="text-danger field-error" v-if="errors.memberDashboardObj">{{ errors.memberDashboardObj }}</span>
                    </div>
                    <div class="form-col"></div>
                </div>

                <!-- Row 3: Status (edit mode only) ---->
                <div class="form-row mb-4" v-if="!isNewMode">
                    <div class="form-col">
                        <label class="field-label">Event Status</label>
                        <div class="d-flex align-items-center gap-3">
                            <span :class="`status-badge status-${currentStatus}`">{{ currentStatus }}</span>
                            <select
                                class="form-control"
                                style="max-width: 220px"
                                :value="currentStatus"
                                :disabled="isUpdatingStatus"
                                @change="changeStatus($event.target.value)"
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="ended">Ended</option>
                            </select>
                            <span v-if="isUpdatingStatus" class="spinner-border spinner-border-sm text-primary" role="status"></span>
                        </div>
                    </div>
                </div>

                <!-- Row 4: Description (full width) -->
                <div class="mb-4">
                    <label class="field-label">Description</label>
                    <textarea
                        v-model="form.description"
                        class="form-control"
                        placeholder="Enter Description"
                        rows="4"
                    ></textarea>
                </div>

                <div class="d-flex gap-3 mt-4">
                    <button class="button primary" :disabled="isSaving" @click="saveEvent">
                        {{ isSaving ? 'Saving...' : 'Save' }}
                    </button>
                    <button class="button secondary" @click="$router.push({ name: 'StreetTeamEventsList' })">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import Multiselect from 'vue-multiselect';
    import { streetTeam, dashboardBuilder } from '@/util/apiRequests';

    // Handles: "YYYY-MM-DD", "YYYY-MM-DDTHH:MM:SS.sssZ", "YYYY-MM-DD HH:MM:SS", JS Date object
    function toDateInput(val) {
        if (!val) return '';
        const str = val instanceof Date ? val.toISOString() : String(val);
        return str.substring(0, 10);
    }

    export default {
        name: 'StreetTeamEventEdit',
        components: { Multiselect },
        data() {
            return {
                isNewMode: true,
                isLoading: true,
                isSaving: false,
                form: {
                    name: '',
                    location: '',
                    event_date: '',
                    description: '',
                    dashboardObj: null,
                    memberDashboardObj: null,
                },
                errors: {},
                dashboardOptions: [],
                currentStatus: 'inactive',
                isUpdatingStatus: false,
            };
        },
        methods: {
            formatDateDisplay(val) {
                if (!val) return '';
                const [y, m, d] = val.split('-');
                return `${m}/${d}/${y}`;
            },
            validateForm() {
                this.errors = {};
                if (!this.form.name || !this.form.name.trim()) {
                    this.errors.name = 'Event Name is required';
                }
                if (!this.form.event_date) {
                    this.errors.event_date = 'Event Date is required';
                }
                if (!this.form.dashboardObj) {
                    this.errors.dashboardObj = 'Street Team Dashboard is required';
                }
                if (!this.form.memberDashboardObj) {
                    this.errors.memberDashboardObj = 'Member Dashboard is required';
                }
                return Object.keys(this.errors).length === 0;
            },
            async saveEvent() {
                if (!this.validateForm()) return;

                const payload = {
                    name: this.form.name,
                    location: this.form.location || null,
                    event_date: this.form.event_date || null,
                    description: this.form.description || null,
                    dashboard_id: this.form.dashboardObj ? this.form.dashboardObj.id : null,
                    member_dashboard_id: this.form.memberDashboardObj ? this.form.memberDashboardObj.id : null,
                };

                try {
                    this.isSaving = true;
                    let res;
                    if (this.isNewMode) {
                        res = await this.$api.post(streetTeam.create(), payload);
                    } else {
                        res = await this.$api.put(streetTeam.update(this.$route.query.id), payload);
                    }

                    if (res.status >= 400) {
                        this.$toasted.error(res.data?.message || 'Failed to save event.');
                        return;
                    }

                    this.$toasted.success(this.isNewMode ? 'Event created successfully!' : 'Event updated successfully!');
                    this.$router.push({ name: 'StreetTeamEventsList' });
                } catch (err) {
                    this.$toasted.error('Failed to save event.');
                } finally {
                    this.isSaving = false;
                }
            },
            async changeStatus(newStatus) {
                if (newStatus === this.currentStatus) return;
                const id = this.$route.query.id;
                try {
                    this.isUpdatingStatus = true;
                    const res = await this.$api.put(streetTeam.updateStatus(id), { status: newStatus });
                    if (res.status >= 400) {
                        this.$toasted.error(res.data?.message || 'Failed to update status.');
                        return;
                    }
                    this.currentStatus = newStatus;
                    this.$toasted.success('Status updated successfully!');
                } catch (err) {
                    this.$toasted.error('Failed to update status.');
                } finally {
                    this.isUpdatingStatus = false;
                }
            },
            async loadDashboards() {
                try {
                    const res = await this.$api.get(dashboardBuilder.getAllTemplateList('completed', 'mobex-health-home'));
                    this.dashboardOptions = res.data?.templateMetaList ?? [];
                } catch (err) {
                    this.dashboardOptions = [];
                }
            },
        },
        async created() {
            try {
                await this.loadDashboards();

                const id = this.$route.query.id;
                this.isNewMode = !id;

                if (!this.isNewMode) {
                    const res = await this.$api.get(streetTeam.getById(id));
                    if (res.status >= 400) {
                        this.$toasted.error('Failed to load event.');
                        return;
                    }
                    const event = res.data?.data ?? res.data;
                    this.form.name = event.name || '';
                    this.form.location = event.location || '';
                    this.form.event_date = toDateInput(event.event_date);
                    this.form.description = event.description || '';
                    this.form.dashboardObj = this.dashboardOptions.find((d) => d.id == event.dashboard_id) || null;
                    this.form.memberDashboardObj = this.dashboardOptions.find((d) => d.id == event.member_dashboard_id) || null;
                    this.currentStatus = event.status || 'active';
                }
            } catch (err) {
                this.$toasted.error('Failed to load event.');
            } finally {
                this.isLoading = false;
            }
        },
    };
</script>

<style scoped>
    .bg-color {
        background-color: rgba(255, 255, 255, 0.9);
    }

    .form-row {
        display: flex;
        gap: 2rem;
    }

    .form-col {
        flex: 1;
        min-width: 0;
    }

    .field-label {
        display: block;
        font-size: 0.95rem;
        font-weight: 600;
        margin-bottom: 6px;
        color: #333;
    }

    .field-error {
        font-size: 0.82rem;
        margin-top: 4px;
        display: block;
    }

    .form-control {
        padding: 10px 12px;
        border-radius: 8px;
        border: 1px solid #ced4da;
        width: 100%;
        font-size: 0.9rem;
        line-height: 1.5;
        transition: border-color 0.15s;
    }

    .form-control:focus {
        outline: none;
        border-color: #5057c3;
        box-shadow: 0 0 0 3px rgba(80, 87, 195, 0.12);
    }

    textarea.form-control {
        resize: vertical;
    }

    .status-badge {
        padding: 3px 10px;
        border-radius: 12px;
        font-size: 0.8rem;
        text-transform: capitalize;
        white-space: nowrap;
    }

    .status-active { background: #d4edda; color: #155724; }
    .status-inactive { background: #e2e3e5; color: #383d41; }
    .status-ended { background: #cce5ff; color: #004085; }
    .status-cancelled { background: #f8d7da; color: #721c24; }


    .date-input-wrapper {
        position: relative;
        width: 100%; 
    }

    .date-display {
        width: 100%;
        cursor: pointer;
        background: #fff;
    }

    .date-icon {
        position: absolute;
        right: -8px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 18px;
        line-height: 1;
        color: #666;
        pointer-events: none;
    }

    .date-hidden-picker {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        opacity: 0;
        cursor: pointer;
    }

    @media (max-width: 640px) {
        .form-row {
            flex-direction: column;
            gap: 1rem;
        }
    }
</style>

